import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../AppContext';
import { api } from '../api';
import { Card, StatCard, Badge } from '../components/UI';

export default function Home() {
  const { user, site } = useApp();
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    api.todaySummary().then(r => r?.ok && setSummary(r.data));
    api.signinsToday().then(r => r?.ok && setEntries(r.data.entries || []));
  }, []);

  const features = site?.features || {};
  const perms = user?.permissions || {};
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const quickLinks = [
    features.signins && { to: '/signins', label: 'Sign-Ins', desc: 'Sign members in', icon: '↵' },
    features.parties && { to: '/party', label: 'Parties', desc: 'Manage bookings', icon: '◇' },
    features.members && perms.can_manage_members && { to: '/members', label: 'Members', desc: 'View & update status', icon: '◈' },
    features.reports && perms.can_view_reports && { to: '/manager-report', label: 'Reports', desc: 'Sign-in history', icon: '◉' },
    features.codes && perms.can_manage_codes && { to: '/code-generator', label: 'Print Cards', desc: 'Generate QR cards', icon: '⊞', external: true },
    perms.can_access_settings && { to: '/settings', label: 'Settings', desc: 'Configure site', icon: '◎' },
  ].filter(Boolean);

  return (
    <div style={{ padding: '28px 24px', maxWidth: 1100, margin: '0 auto', animation: 'fadeIn 0.2s ease' }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: 'var(--fg)', letterSpacing: '-0.03em', fontFamily: 'var(--font)', marginBottom: 4 }}>
          {greeting}, {user?.username}
        </h1>
        <p style={{ fontSize: 13, color: 'var(--fg3)', fontFamily: 'var(--font)' }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          {site?.name && <span style={{ marginLeft: 8, paddingLeft: 8, borderLeft: '1px solid var(--border)' }}>{site.name}</span>}
        </p>
      </div>

      {/* Stats */}
      {summary && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
          <StatCard label="People today"  value={summary.total_people}  />
          <StatCard label="Members"       value={summary.total_members} />
          <StatCard label="Guests"        value={summary.total_guests}  color="var(--warning)" />
          {features.revenue && (
            <StatCard label="Revenue" value={`$${(summary.total_revenue || 0).toFixed(2)}`} color="var(--success)" sub={`$${(summary.cash_revenue||0).toFixed(2)} cash · $${(summary.card_revenue||0).toFixed(2)} card`} />
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>

        {/* Quick actions */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--fg3)', marginBottom: 12, fontFamily: 'var(--font)' }}>Quick Access</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
            {quickLinks.map(({ to, label, desc, icon, external }) => {
              const content = (
                <div style={{ padding: '16px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', textDecoration: 'none', display: 'block', cursor: 'pointer', transition: 'all 0.15s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow)'; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{ fontSize: 18, color: 'var(--accent)', marginBottom: 8, fontFamily: 'var(--mono)' }}>{icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)', fontFamily: 'var(--font)', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 11, color: 'var(--fg3)', fontFamily: 'var(--font)' }}>{desc}</div>
                </div>
              );
              return external
                ? <a key={to} href={api.flaskUrl(to)} style={{ textDecoration: 'none' }}>{content}</a>
                : <Link key={to} to={to} style={{ textDecoration: 'none' }}>{content}</Link>;
            })}
          </div>
        </div>

        {/* Today's log */}
        <Card>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--fg3)', fontFamily: 'var(--font)' }}>Today's Sign-Ins</span>
            <Badge variant="blue">{entries.length}</Badge>
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {entries.length === 0
              ? <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--fg3)', fontSize: 12 }}>No sign-ins yet today.</div>
              : entries.slice(0, 20).map(e => (
                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 16px', borderBottom: '1px solid var(--border-light)' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', fontFamily: 'var(--font)' }}>{e.lastname}</div>
                    <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 1 }}>
                      {e.member_people}m{e.guest_count > 0 ? ` · ${e.guest_count}g` : ''}
                      {e.party_flag ? <span style={{ marginLeft: 4, color: 'var(--warning)' }}>· party</span> : null}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {features.revenue && <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', fontFamily: 'var(--mono)' }}>${(e.total_charge || 0).toFixed(2)}</div>}
                    <Badge variant={e.payment_method === 'card' ? 'blue' : 'green'} style={{ marginTop: 2 }}>{e.payment_method}</Badge>
                  </div>
                </div>
              ))}
          </div>
          {entries.length > 20 && (
            <Link to="/signins" style={{ display: 'block', padding: '10px 16px', textAlign: 'center', fontSize: 12, color: 'var(--accent)', textDecoration: 'none', borderTop: '1px solid var(--border)', fontWeight: 500 }}>
              View all {entries.length} sign-ins
            </Link>
          )}
        </Card>
      </div>
    </div>
  );
}
